import { useState, useRef } from 'react'
import { ChartSection } from './sections/ChartSection/ChartSection'
import './DashboardTab.scss'
import { StatisticsSection } from './sections/StatisticsSection/StatisticsSection'
import WidgetsSection from './sections/WidgetsSection'
import Filters from 'pages/HomePage/components/Filters'
import { Drawer } from 'shared/Drawer'
import { useFilters } from 'core/contexts/FilterContext'
import { useGlobalTimezone } from 'core/contexts/GlobalTimezoneContext'
import { analyzeDashboard } from 'core/api/ai'
import SkeletonBlock from 'shared/SkeletonBlock'
import ReactMarkdown from 'react-markdown'
import { Modal, useModal } from 'shared/Modal'
import { AiConsentModal } from './components/AiConsentModal/AiConsentModal'

export function DashboardTab() {
  const { filters } = useFilters()
  const { timeZone } = useGlobalTimezone()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<string | null>(null)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const lastAnalyzedKey = useRef<string | null>(null)

  const aiConsentModal = useModal<boolean>()

  const handleAnalyze = async () => {
    const accepted = await aiConsentModal.open()
    if (!accepted) return

    const zoneOffsetMinutes = Math.round(timeZone.offsetMs / 60000)
    const currentKey = JSON.stringify({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      symbols: [...filters.symbols].sort(),
      journalId: filters.journalId,
      zoneOffsetMinutes,
    })

    if (analysisResult !== null && currentKey === lastAnalyzedKey.current) {
      setIsDrawerOpen(true)
      return
    }

    setIsDrawerOpen(true)
    setIsLoading(true)
    setAnalysisResult(null)
    setAnalysisError(null)
    try {
      const result = await analyzeDashboard(filters, zoneOffsetMinutes)
      setAnalysisResult(result)
      lastAnalyzedKey.current = currentKey
    } catch {
      setAnalysisError('Failed to fetch analysis. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="DashboardTab">
      <Filters onAnalyze={handleAnalyze} />
      <StatisticsSection />
      <ChartSection label="P&L history" />
      <WidgetsSection />
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="AI Analysis"
        copyContent={analysisResult}
        feedbackLink="https://docs.google.com/forms/d/e/1FAIpQLSdxk7iZ3_BCiQb0O5BEcH3vtKlkMWPOHOdO_kcKXF-149l6sQ/viewform"
      >
        {isLoading && (
          <div className="Drawer__skeletons">
            <SkeletonBlock style={{ height: 20, borderRadius: 4 }} />
            <SkeletonBlock style={{ height: 20, borderRadius: 4, width: '85%' }} />
            <SkeletonBlock style={{ height: 20, borderRadius: 4, width: '70%' }} />
            <SkeletonBlock style={{ height: 20, borderRadius: 4, marginTop: 12 }} />
            <SkeletonBlock style={{ height: 20, borderRadius: 4, width: '90%' }} />
            <SkeletonBlock style={{ height: 20, borderRadius: 4, width: '60%' }} />
          </div>
        )}
        {analysisError && <div className="Drawer__error">{analysisError}</div>}
        {analysisResult && <ReactMarkdown>{analysisResult}</ReactMarkdown>}
      </Drawer>

      <Modal controller={aiConsentModal}>
        <AiConsentModal controller={aiConsentModal} />
      </Modal>
    </div>
  )
}
